import { Button, ButtonOwnProps } from './Button.js';
import { type ButtonSize, type ButtonVariant } from './types.js';
import { type IconSvgProps } from '@contra/icons/icons';
import { type ComponentType, ForwardedRef, type MouseEvent } from 'react';
import { css, styled } from 'styled-components';

const getCircularButtonSize = (size: ButtonSize | number) => {
  if (typeof size === 'number') {
    return size;
  }

  switch (size) {
    case 'large':
    case 'medium':
      return 48;
    case 'small':
      return 40;
    case 'xsmall':
      return 32;
    default:
      return size;
  }
};

const CircleButton = styled(Button)<{ $noCursor?: boolean; $size?: number }>`
  ${({ $noCursor = false, $size }) => css`
    border-radius: 50%;
    height: ${$size}px;
    width: ${$size}px;
    padding: 0px;
    flex-shrink: 0;

    ${$noCursor
      ? css`
          cursor: default;
          pointer-events: none;
        `
      : ''}

    * {
      line-height: 1;
    }

    svg {
      display: block;
      flex-shrink: 0;
    }
  `}
`;

type CircularIconButtonProps = {
  readonly 'aria-label'?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly icon: ComponentType<IconSvgProps>;
  readonly iconSize?: number;
  readonly noCursor?: boolean;
  readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly ref?: ForwardedRef<HTMLButtonElement>;
  readonly size?: ButtonSize | number;
  readonly type?: ButtonOwnProps['type'];
  readonly variant?: ButtonVariant;
};

export const CircularIconButton = ({
  'aria-label': ariaLabel,
  className,
  disabled,
  icon: Icon,
  iconSize,
  noCursor = false,
  onClick,
  ref,
  size = 'medium',
  type = 'button',
  variant = 'V2TertiaryOutline',
}: CircularIconButtonProps) => {
  const circleSize = getCircularButtonSize(size);
  const innerIconSize = iconSize ? iconSize : circleSize / 2;

  return (
    <CircleButton
      $noCursor={noCursor}
      $size={circleSize}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      icon={undefined}
      onClick={onClick}
      ref={ref}
      removeMinWidth
      type={type}
      variant={variant}
    >
      <Icon size={innerIconSize} />
    </CircleButton>
  );
};
